import { Button } from "@/src/components/buttons";
import { Card, CheckboxCard } from "@/src/components/cards";
import { openDrawer } from "@/src/components/dialogs";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import { ReorderableItems } from "@/src/components/reorderable";
import { useState } from "react";
import { uid, getItemId } from "../utils/utils";

type LoadItemsType<T> = (
  limit: number,
  offset: number,
  searchKey: string,
  viewType: "all" | "selected",
  ids: string[] | null | undefined
) => Promise<(T | null | undefined)[] | null | undefined>;

export function MultiPicker<T>({
  title,
  value,
  isReorderable,
  queryKey,
  onAddClick,
  getChecboxCardContent,
  getChild,
  loadItems,
  onChange,
}: {
  title: string;
  value: (T | null | undefined)[];
  isReorderable?: boolean;
  queryKey: any[];
  onAddClick?: () => void;
  getChecboxCardContent: (item: T) => React.ReactNode;
  loadItems: LoadItemsType<T>;
  onChange: (newValue: (T | null | undefined)[]) => void;
  getChild: (item: T) => React.ReactNode;
}) {
  const [localValue, setLocalValue] = useState(value || []);
  function onLocalChange(newValue: (T | null | undefined)[]) {
    setLocalValue(newValue);
    onChange(newValue);
  }
  return (
    <div className="c1 gap5">
      <div className="label">{title}</div>
      <div className="c1 gap10">
        {isReorderable == true && (
          <ReorderableItems
            listId={`${title}List`}
            listType={`${title}List`}
            items={localValue}
            onReorder={(newItems) => {
              onLocalChange(newItems);
            }}
          >
            {localValue.map((item) => {
              if (!item) return null;
              return getChild(item);
            })}
          </ReorderableItems>
        )}
        {isReorderable != true &&
          localValue.map((item) => {
            if (!item) return null;
            return getChild(item);
          })}
        <Button
          type="tertiary"
          onClick={async () => {
            await openMultiPickerList({
              title,
              queryKey,
              onAddClick,
              getChecboxCardContent,
              loadItems,
              current: localValue,
              onSave: (newValue) => {
                onLocalChange(newValue);
              },
            });
          }}
        >
          Ajouter des {title}
        </Button>
      </div>
    </div>
  );
}

export function MultiPickerList<T extends any>({
  drawerId,
  queryKey,
  onAddClick,
  loadItems,
  enableSearch,
  getChecboxCardContent,
  current,
  onChange,
}: {
  drawerId?: string;
  queryKey: any[];
  enableSearch?: boolean;
  loadItems: LoadItemsType<T>;
  onAddClick?: (searchKey: string, drawerId: string) => void;
  getChecboxCardContent: (item: T, searchKey: string) => React.ReactNode;
  current: (T | null | undefined)[];
  onChange: (newValue: (T | null | undefined)[]) => void;
}) {
  const [reload, setReload] = useState(0);
  const [value, setValue] = useState(current);
  const [searchKey, setSearchKey] = useState("");
  const [viewType, setViewType] = useState<"all" | "selected">("all");

  return (
    <div className="c1 gap15">
      {enableSearch !== false && (
        <>
          <div className="bold">{value.length} sélectionnés</div>
          <div className="c2 gap10">
            <Button
              type={viewType == "all" ? "primary" : "tertiary"}
              onClick={() => {
                setViewType("all");
                setSearchKey("");
                setReload(reload + 1);
              }}
            >
              Tous
            </Button>
            <Button
              type={viewType == "selected" ? "primary" : "tertiary"}
              onClick={() => {
                setViewType("selected");
                setSearchKey("");
                setReload(reload + 1);
              }}
            >
              Sélectionnés
            </Button>
          </div>
        </>
      )}

      <InfiniteFetchableList
        key={`MultiPickerList-${reload}`}
        queryKey={[...queryKey, searchKey, reload, viewType]}
        getFunction={(limit, offset, searchKey) =>
          loadItems(
            limit,
            offset,
            searchKey || "",
            viewType,
            value.map((item) => getItemId(item)) || []
          )
        }
        shouldFocus={true}
        searchKey={searchKey}
        onSearch={
          enableSearch === false
            ? undefined
            : (newValue) => {
                setSearchKey(newValue || "");
              }
        }
        onClick={
          onAddClick
            ? () => {
                onAddClick(searchKey, drawerId || "");
                setReload(reload + 1);
              }
            : undefined
        }
      >
        {(data) => (
          <div className={`c1 gap10`}>
            {data?.map((item) => {
              if (!item) return null;
              const isSelected = value.find(
                (u) => getItemId(u) == getItemId(item)
              );
              // if (viewType == "selected" && !isSelected) return null;
              let checkboxCardContent = getChecboxCardContent(item, searchKey);
              if (!checkboxCardContent) return null;
              return (
                <CheckboxCard
                  key={uid()}
                  color="simple"
                  title={null}
                  status={isSelected ? "active" : "inactive"}
                  onClick={async () => {
                    let newValue = value;
                    if (isSelected) {
                      newValue = value.filter(
                        (u) => getItemId(u) != getItemId(item)
                      );
                    } else {
                      newValue = [...value, item];
                    }
                    setValue([...newValue]);
                    onChange([...newValue]);
                  }}
                >
                  {getChecboxCardContent(item, searchKey)}
                </CheckboxCard>
              );
            })}
          </div>
        )}
      </InfiniteFetchableList>
    </div>
  );
}

export async function openMultiPickerList<T>({
  title,
  queryKey,
  loadItems,
  getChecboxCardContent,
  current,
  onSave,
  onAddClick,
}: {
  title: string;
  queryKey: any[];
  loadItems: LoadItemsType<T>;
  getChecboxCardContent: (item: T, searchKey: string) => React.ReactNode;
  current: (T | null | undefined)[];
  onSave: (newValue: (T | null | undefined)[]) => void;
  onAddClick?: (searchKey: string, drawerId: string) => void;
}) {
  let value = current;
  const drawerId = uid();
  return await openDrawer({
    id: drawerId,
    title: `${title}`,
    content: (
      <MultiPickerList
        drawerId={drawerId}
        queryKey={queryKey}
        onAddClick={onAddClick}
        loadItems={loadItems}
        getChecboxCardContent={getChecboxCardContent}
        current={value}
        onChange={(newValue) => {
          value = newValue;
        }}
      />
    ),
    onConfirm: async () => {
      await onSave(value);
    },
  });
}

{
  // EXAMPLE
  /*  
  <MultiPicker
        title="Produits"
        queryKey={["tenderItems"]}
        value={value.products}
        isReorderable={true}
        onAddClick={async () => {
          openProductForm({
            current: null,
            onSave: async (newValue) => {
              await apiFetch("products", "addOne", { item: newValue });
            },
          });
        }}
        loadItems={async (limit, offset, searchKey, viewType, ids) => {
          return await apiFetch("products", "loadMany", {
            searchKey,
            ids: viewType == "selected" ? ids : null,
            limit,
            offset,
          });
        }}
        getChecboxCardContent={(item) => {
          return (
            <div>
              <div className="bold">{item.name}</div>
              <div className="c1 gap5">
                <div className="label">Prix unitaire</div>
                <div className="value">{item.price}</div>
              </div>
            </div>
          );
        }}
        getChild={(item) => {
          return (
            <Card color="blue" key={uid()} title={item.name}>
              Reordonnable
            </Card>
          );
        }}
        onChange={(newValue) => {
          onLocalChange("products", newValue);
        }}
      ></MultiPicker> */
}
